import React, { useMemo, useState } from "react";

const MovieShowCalendar = ({
  shows = [],
  showDates = [],
  selectedDate,
  onDateChange,
  onClose,
}) => {
  const today = new Date();

  const toDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const normalizeDate = (value) => {
    if (!value) return "";

    const dateObj = new Date(value);

    if (Number.isNaN(dateObj.getTime())) {
      return value;
    }

    return toDateKey(dateObj);
  };

  const todayKey = toDateKey(today);

  const availableDateSet = useMemo(() => {
    const datesFromShowDates = showDates
      .map((showDate) =>
        normalizeDate(
          typeof showDate === "string"
            ? showDate
            : showDate?.date ||
                showDate?.showDate ||
                showDate?.show_date ||
                showDate?.start_date ||
                showDate?.available_date
        )
      )
      .filter(Boolean);

    const datesFromShows = shows
      .map((show) =>
        normalizeDate(
          show.date ||
            show.showDate ||
            show.show_date ||
            show.start_date ||
            show.available_date
        )
      )
      .filter(Boolean);

    return new Set([...datesFromShowDates, ...datesFromShows]);
  }, [showDates, shows]);

  const initialMonthDate = selectedDate ? new Date(selectedDate) : today;

  const [currentMonth, setCurrentMonth] = useState(
    new Date(initialMonthDate.getFullYear(), initialMonthDate.getMonth(), 1)
  );

  const monthLabel = currentMonth.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);

    const startDay = firstDayOfMonth.getDay();
    const totalDays = lastDayOfMonth.getDate();

    const days = [];

    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }

    for (let day = 1; day <= totalDays; day++) {
      const dateObj = new Date(year, month, day);
      const value = toDateKey(dateObj);

      const isFutureOrToday = value >= todayKey;

      days.push({
        date: day,
        value,
        isToday: value === todayKey,
        isSelected: value === selectedDate,
        isAvailable: availableDateSet.has(value) && isFutureOrToday,
      });
    }

    return days;
  }, [currentMonth, selectedDate, availableDateSet, todayKey]);

  const goToPreviousMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  const goToNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
  };

  const handleDateSelect = (day) => {
    if (!day?.isAvailable) return;

    onDateChange(day.value);
    onClose();
  };



  return (
    <div className="date-calendar-overlay">
      <div className="date-calendar-popup full-month-calendar-popup">
        <button
          type="button"
          className="date-calendar-close"
          onClick={onClose}
        >
          ×
        </button>

        <div className="full-calendar-top">
          <div>
            <span>Select Date</span>
            <h3>Other Dates</h3>
          </div>
        </div>

        <div className="calendar-month-controller">
          <button type="button" onClick={goToPreviousMonth}>
            <i className="fa-solid fa-chevron-left"></i>
          </button>

          <strong>{monthLabel}</strong>

          <button type="button" onClick={goToNextMonth}>
            <i className="fa-solid fa-chevron-right"></i>
          </button>
        </div>

        <div className="calendar-week-row">
          <span>Sun</span>
          <span>Mon</span>
          <span>Tue</span>
          <span>Wed</span>
          <span>Thu</span>
          <span>Fri</span>
          <span>Sat</span>
        </div>

        <div className="calendar-month-grid">
          {calendarDays.map((day, index) =>
            day ? (
              <button
                type="button"
                key={day.value}
                disabled={!day.isAvailable}
                className={[
                  "calendar-day-btn",
                  day.isAvailable ? "available" : "unavailable",
                  day.isSelected ? "selected" : "",
                  day.isToday ? "today" : "",
                ].join(" ")}
                onClick={() => handleDateSelect(day)}
              >
                <span>{day.date}</span>

                {day.isAvailable && <small>Available</small>}
              </button>
            ) : (
              <div key={`empty-${index}`} className="calendar-empty-cell"></div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default MovieShowCalendar;